import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { timetableService } from '../../lib/api/services/timetable-service'
import { useTimeSlots } from '@/components/adminPage/timetable/logic/timeslotes'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import {School, ArrowLeft } from 'lucide-react'

// Define the response type for the teacher timetable API
interface TeacherTimetableResponse {
  scheduleData: Array<{
    class: string
    [day: string]: any[] | string // Can be either an array of assignments or the class name
  }>
}

const DAYS_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const TeacherTimetable: React.FC = () => {
  const [data, setData] = useState<TeacherTimetableResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation('teacher')

  // Use time slots hook (same as admin)
  const {
    timeSlotData,
    isLoading: isLoadingTimeSlots,
  } = useTimeSlots()

  const translateDay = (day: string) =>
    t(`timetable.days.${day?.toLowerCase?.()}`, day)

  useEffect(() => {
    const fetchTeacherTimetable = async () => {
      setLoading(true)
      try {
        // Get the teacher timetable using the timetable service
        const response = await timetableService.getTeacherTimetable()
    // console.logTeacher timetable data:', response)
        setData(response)

        setLoading(false)
        setError(null)
      } catch (err) {
        // console.error('Error fetching teacher timetable:', err)
        setError(t('timetable.error'))
        setLoading(false)
      }
    }

    fetchTeacherTimetable()
  }, [t])

  // Format time for display
  const formatTimeForDisplay = (timeStr: string): string => {
    if (timeStr.includes(':')) {
      const [hours, minutes] = timeStr.split(':').map(Number)
      if (minutes === 0) {
        return hours.toString()
      }
      return timeStr
    }
    return timeStr
  }

  // Get time slots
  const getTimeSlots = () => {
    if (timeSlotData.length === 0) return []
    const sortedSlots = [...timeSlotData].sort((a, b) => a.period - b.period)
    return sortedSlots.map(
      (slot) =>
        `${formatTimeForDisplay(slot.start)} => ${formatTimeForDisplay(slot.end)}`
    )
  }

  // Get lessons for a specific day and time (aggregate across all classes)
  const getLessons = (day: string, time: string) => {
    if (!data || !data.scheduleData || data.scheduleData.length === 0) return []
    
    const lessons: any[] = []
    data.scheduleData.forEach((classData) => {
      if (classData[day] && Array.isArray(classData[day])) {
        classData[day].forEach((lesson: any) => {
          if (lesson && (lesson.time === time || lesson.timeSlot === time)) {
            lessons.push({
              ...lesson,
              className: classData.class,
            })
          }
        })
      }
    })
    
    return lessons
  }

  // Render timetable
  const renderTimetable = () => {
    const days = DAYS_ORDER
    const timeSlots = getTimeSlots()

    if (days.length === 0 || timeSlots.length === 0) {
      return (
        <div className="text-center p-8">
          <p className="text-muted-foreground">
            {t('timetable.noSchedule.title')}
          </p>
        </div>
      )
    }

    return (
      <div className="overflow-x-auto timetable-grid">
        <table className="w-full border-collapse min-w-[700px] md:min-w-[800px]">
          <thead>
            <tr>
              <th className="border border-gray-300 p-3 bg-gray-100 text-gray-800 font-semibold text-sm md:text-base sticky left-0 z-10">
                {t('timetable.table.headers.time')}
              </th>
              {days.map((day) => (
                <th
                  key={day}
                  className="border border-gray-300 p-3 bg-gray-100 text-gray-800 font-semibold min-w-[100px] md:min-w-[120px] lg:min-w-[150px] text-xs md:text-sm"
                >
                  {translateDay(day)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((time) => {
              const isBreak = timeSlotData.some((slot) => {
                const formattedTime = `${formatTimeForDisplay(slot.start)} => ${formatTimeForDisplay(slot.end)}`
                return formattedTime === time && slot.isBreak
              })

              return (
                <tr key={time} className={isBreak ? 'bg-yellow-50' : ''}>
                  <td
                    className={`border border-gray-300 p-3 text-sm md:text-base font-medium sticky left-0 z-10 ${
                      isBreak 
                        ? 'bg-yellow-100 text-gray-700' 
                        : 'bg-white text-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="whitespace-nowrap">{time}</span>
                      {isBreak && (
                        <span className="inline-flex items-center rounded bg-yellow-200 text-gray-700 px-2 py-1 text-xs font-medium">
                          {t('timetable.break')}
                        </span>
                      )}
                    </div>
                  </td>
                  {days.map((day) => {
                    const lessons = getLessons(day, time)
                    return (
                      <td
                        key={`${day}-${time}`}
                        className="border border-gray-300 p-2 md:p-3 align-top min-w-[120px] bg-white"
                      >
                        {isBreak ? (
                          <div className="text-center text-gray-700 text-sm py-2 bg-yellow-50 rounded">
                            {t('timetable.breakTime')}
                          </div>
                        ) : lessons.length > 0 ? (
                          <div className="space-y-2">
                            {lessons.map((lesson, idx) => (
                              <div
                                key={idx}
                                className="rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-200 bg-gray-50 border border-gray-200"
                              >
                                <div className="font-semibold text-sm md:text-base mb-2 text-gray-800">
                                  {lesson.subject}
                                </div>
                                <div className="text-xs md:text-sm text-gray-600">
                                  📚 {lesson.className || lesson.class}
                                </div>
                                {lesson.salle && (
                                  <div className="text-xs md:text-sm text-gray-600 flex items-center mt-2">
                                    <School className="h-3.5 w-3.5 mr-1.5" />
                                    <span>{lesson.salle}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="h-16 flex items-center justify-center text-xs text-gray-400">
                            {t('timetable.free')}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  if (loading || isLoadingTimeSlots)
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  if (error) return <div className="p-4 text-red-500">{error}</div>
  if (!data)
    return (
      <div className="p-4">
        {t('timetable.noData.title')} {t('timetable.noData.contactAdmin')}
      </div>
    )
  if (
    !data.scheduleData ||
    !Array.isArray(data.scheduleData) ||
    data.scheduleData.length === 0
  ) {
    return (
      <div className="p-4">
        {t('timetable.noSchedule.title')} {t('timetable.noSchedule.contactAdmin')}
      </div>
    )
  }

  return (
    <div className="w-full space-y-6 p-4 md:p-6 bg-white min-h-screen">
      <Button
        onClick={() => window.history.back()}
        variant="outline"
        className="flex items-center gap-2 mt-2 ml-2"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('timetable.backButton')}
      </Button>
      <Card className="w-full border border-gray-200 bg-white">
        <CardHeader className="bg-white border-b border-gray-200 pb-4">
          <CardTitle className="text-xl md:text-2xl font-semibold">
            {t('timetable.title')}
          </CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            {t('timetable.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 md:p-6" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
          <ScrollArea className="h-[calc(100vh-300px)] max-h-[700px]">
            {renderTimetable()}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}

export default TeacherTimetable
